import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Grid,
  Breadcrumbs,
  Box,
  FormControlLabel,
  Checkbox
} from "@mui/material";
import GridTable from "../../../components/GridTable";
import Button from "../../../components/Button";
import Dialog from "../../../components/Dialog";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import CopyrightIcon from "@mui/icons-material/Copyright";
import ContentCopyRounded from "@mui/icons-material/ContentCopyRounded";
import { GridCellParams } from "@mui/x-data-grid";
import ActionMenu from "../../../components/ActionMenu";
import TextField from "../../../components/TextField";
import Loader from "../../../components/Loader";
import {
  getCropConfigDetailsByCropId,
  getCropConfigById,
  deleteCropConfig,
  updateCropConfigById,
} from "./api";
import {
  ApiResponse,
  getCropConfigDetailsByCropIdResponse,
  UpdateCropConfigParams,
} from "./interface";
import { clearRedisCache } from "../../company/config/api"
import { successMessage, errorMessage } from "../../../components/Toaster";
import { getErrorMessage } from "../../../utils/error";

const ConfigDetails: React.FC = () => {
  const navigate = useNavigate();
  const { crop_id } = useParams<{ crop_id: string }>();
  const { crop_name } = useParams<{ crop_name: string }>();
  const [cropConfigs, setCropConfigs] = useState<getCropConfigDetailsByCropIdResponse[]>([]);
  const [dialogType, setDialogType] = useState<"delete" | "paste" | null>(null);
  const [itemToDelete, setItemToDelete] = useState<GridCellParams | null>(null);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [cropConfigIdToCopy, setCropConfigIdToCopy] = useState<string | null>(null);
  const [selectedOptions, setSelectedOptions] = useState({
    crop_class_config: false,
    crop_instruction_config: false,
    response_config: false,
    request_config: false,
  });
  const [loading, setLoading] = useState({
    update: false,
    delete: false,
    create: false,
    fetch: false,
  });

  const columns = [
    {
      field: "variety_name",
      headerName: "Variety Name",
      headerClassName: "table-header",
      flex: 1,
    },
    {
      field: "machine_name",
      headerName: "Machine Name",
      headerClassName: "table-header",
      flex: 1,
    },
    {
      field: "type",
      headerName: "Flow Type",
      headerClassName: "table-header",
      flex: 1,
    },
    {
      field: "actions",
      headerName: "Actions",
      headerClassName: "table-header",
      renderCell: (params: GridCellParams) => (
        <ActionMenu
          params={params}
          actions={[
            {
              icon: <EditIcon color="primary" />,
              tooltip: "Edit",
              onClick: handleEdit,
            },
            {
              icon: <DeleteIcon color="error" />,
              tooltip: "Delete",
              onClick: handleDelete,
            },
            {
              icon: <CopyrightIcon color="primary" />,
              tooltip: "Copy",
              onClick: handleCopy,
            },
            {
              icon: <ContentCopyRounded color="inherit" />,
              tooltip: "Paste",
              onClick: handlePaste,
            },
          ]}
        />
      ),
      sortable: false,
      filterable: false,
      flex: 1,
    },
  ];

  useEffect(() => {
    if (crop_id) {
      fetchCropConfig(crop_id);
    }
  }, [crop_id]);

  const fetchCropConfig = async (cropId: string) => {
    setLoading((prev) => ({ ...prev, fetch: true }));
    try {
      const response: ApiResponse<getCropConfigDetailsByCropIdResponse[]> =
        await getCropConfigDetailsByCropId(cropId);
      if (response.data && Array.isArray(response.data)) {
        setCropConfigs(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch crop configuration:", error);
    }
    setLoading((prev) => ({ ...prev, fetch: false }));
  };

  const handleAddClick = () => {
    navigate(`/crops/${crop_id}/${crop_name}/crop_config/add`);
  };

  const handlePasteForMultipleRow = async () => {
    setLoading((prev) => ({ ...prev, update: true }));
    try {
      const clipboardText = await navigator.clipboard.readText();
      const parsedData = JSON.parse(clipboardText);
      if (!parsedData) {
        throw new Error("Invalid data on clipboard");
      }
      const cropConfigIds = selectedRowIds;
      let allSuccessful = true;

      for (const cropConfigId of cropConfigIds) {
        const existingData = await getCropConfigById(cropConfigId);
        const updatedData: UpdateCropConfigParams = {
          ...existingData.data,
          ...parsedData,
        };
        const response = await updateCropConfigById(updatedData);
        if (!response.status) {
          allSuccessful = false;
        }
      }
      if (allSuccessful) {
        successMessage("Successfully pasted the data")
      } else {
        errorMessage("Failed to past data for some IDs")
      }
    } catch (error) {
      console.error("Error pasting crop config:", error);
      errorMessage(getErrorMessage(error) || 'Error pasting crop config. Please try again.');
    }
    setLoading((prev) => ({ ...prev, update: false }));
  };

  const handleCopy = (params: GridCellParams) => {
    setCropConfigIdToCopy(params.row.crop_config_id);
    setCopyDialogOpen(true);
  };

  const handlePaste = async (params: GridCellParams) => {
    const clipboardText = await navigator.clipboard.readText();
    const existingData = await getCropConfigById(params.row.crop_config_id);
    const parsedData = JSON.parse(clipboardText);
    if (!parsedData) {
      throw new Error("Invalid data on clipboard");
    }
    const updatedData: UpdateCropConfigParams = {
      ...existingData.data,
      ...parsedData,
    };
    try {
      const response = await updateCropConfigById(
        updatedData
      );
      if (response.status) {
        successMessage("Successfully pasted the data")
      }
    } catch (error) {
      console.error("Error pasting crop config:", error);
      errorMessage(getErrorMessage(error) || 'Error pasting crop config. Please try again.');
    }
  };

  const handleEdit = (params: GridCellParams) => {
    const varietyName = params.row.variety_name;
    const machineName = params.row.machine_name;
    const cropConfigId = params.row.crop_config_id
    navigate(
      `/crops/${crop_id}/${crop_name}/${varietyName}/${machineName}/crop_config/edit/${cropConfigId}`,
    );
  };

  const copyCropConfig = async () => {
    if (!cropConfigIdToCopy) return;
    try {
      const response = await getCropConfigById(cropConfigIdToCopy);
      const configData = Array.isArray(response.data)
        ? response.data[0]
        : response.data;

      const filteredData = {
        ...(selectedOptions.crop_class_config && {
          crop_class_config: configData.crop_class_config,
        }),
        ...(selectedOptions.crop_instruction_config && {
          crop_instruction_config: configData.crop_instruction_config,
        }),
        ...(selectedOptions.response_config && {
          response_config: configData.response_config,
        }),
        ...(selectedOptions.request_config && {
          request_config: configData.request_config,
        }),
      };
      await navigator.clipboard.writeText(JSON.stringify(filteredData));
      successMessage("Configurations copied to clipboard successfully!")
    } catch (error) {
      console.error("Error copying crop configs:", error);
      errorMessage(getErrorMessage(error) || 'Error copying crop configs');
    } finally {
      setCropConfigIdToCopy(null);
      handleCloseDialog();
    }
  };

  const confirmDelete = async () => {
    if (itemToDelete) {
      setLoading((prev) => ({ ...prev, delete: true }));
      try {
        const result = await deleteCropConfig(itemToDelete.row.crop_config_id);
        if (result.status) {
          setCropConfigs(
            cropConfigs.filter(
              (config) =>
                config.crop_config_id !== itemToDelete.row.crop_config_id
            )
          );
          successMessage(result.message)
        }
      } catch (error) {
        console.error("Error deleting crop config:", error);
        errorMessage(getErrorMessage(error) || 'Error deleting crop config');
      }
      setLoading((prev) => ({ ...prev, delete: false }));
      setDialogType(null);
    }
    setItemToDelete(null);
  };

  const handleCloseDialog = () => {
    setSelectedOptions({
      crop_class_config: false,
      crop_instruction_config: false,
      response_config: false,
      request_config: false
    });
    setCopyDialogOpen(false);
  };

  const handleDelete = (params: GridCellParams) => {
    setItemToDelete(params);
    setDialogType("delete");
  };

  const handlePasteClick = () => {
    setDialogType("paste");
  };

  const handleClearCache = async () => {
    try {
      const response = await clearRedisCache();
      if (response.status) {
        successMessage("Cache cleared successfully!")
      }
    } catch (error) {
      console.error("Error clearing cache.:", error);
      errorMessage(getErrorMessage(error) || 'Error clearing cache.');
    }
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedOptions({
      ...selectedOptions,
      [event.target.name]: event.target.checked,
    });
  };

  const handleDialogClose = () => {
    setDialogType(null);
    setItemToDelete(null);
  };

  const handleConfirm = () => {
    if (dialogType === "delete") {
      confirmDelete();
    } else if (dialogType === "paste") {
      handlePasteForMultipleRow();
    }
    setDialogType(null);
  };

  return (
    <>
      {loading.fetch ? (
        <Loader color="primary" size={40} />
      ) : (
        <Box>
          <main className="m-a-20">
            <div
              style={{ display: "flex", alignItems: "center", marginBottom: "30px" }}
            >
              <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                aria-label="breadcrumb"
                style={{ fontSize: "18px", fontWeight: "bold" }}
              >
                <Link color="primary" to="/crops">
                  Crops
                </Link>
                <Link color="primary" to={`/crops`}>{crop_name}</Link>
                <Link color="primary" to={`/crops/${crop_id}/${crop_name}/crop_config`}>
                  Crop Configurations
                </Link>
              </Breadcrumbs>
            </div>
            <div className="d-flex justify-end">
              <div className="d-flex justify-between w-100">
                <TextField
                  className="w-25"
                  label="Search"
                  variant="outlined"
                  value={searchQuery}
                  size="small"
                  startIcon={<SearchIcon />}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <div>
                  <Button
                    label="Add"
                    variant="contained"
                    color="primary"
                    onClick={handleAddClick}
                  />
                  <Button
                    label="Paste"
                    variant="contained"
                    color="primary"
                    onClick={handlePasteClick}
                    style={{ marginLeft: 15 }}
                  />
                  <Button
                    label="Clear Cache"
                    variant="contained"
                    color="error"
                    onClick={handleClearCache}
                    style={{ marginLeft: 15 }}
                  />
                </div>
              </div>
            </div>
            <div className="ag-theme-alpine m-t-20" style={{ height: '100%', width: '100%' }}>
              <GridTable
                rows={cropConfigs}
                columns={columns}
                pageSize={10}
                checkboxSelection
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                getRowId={(row) => row.crop_config_id}
                onSelectionModelChange={(newSelectionModel) => {
                  setSelectedRowIds(newSelectionModel);
                }}
              />
            </div>
            <Dialog
              open={copyDialogOpen}
              onClose={handleCloseDialog}
              onConfirm={copyCropConfig}
              title="Copy Crop Configurations"
              confirmButtonText="Copy"
              cancelButtonText="Cancel"
              icon={<ContentCopyRounded />}
              message={
                <Box padding={4} sx={{ display: "flex" }}>
                  <Grid size={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="request_config"
                          checked={selectedOptions.request_config}
                          onChange={handleCheckboxChange}
                        />
                      }
                      label="Request Config"
                    />
                  </Grid>
                  <Grid size={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="crop_class_config"
                          checked={selectedOptions.crop_class_config}
                          onChange={handleCheckboxChange}
                        />
                      }
                      label="Crop Class Config"
                    />
                  </Grid>
                  <Grid size={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="crop_instruction_config"
                          checked={selectedOptions.crop_instruction_config}
                          onChange={handleCheckboxChange}
                        />
                      }
                      label="Crop Instruction Config"
                    />
                  </Grid>
                  <Grid size={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="response_config"
                          checked={selectedOptions.response_config}
                          onChange={handleCheckboxChange}
                        />
                      }
                      label="Response Config"
                    />
                  </Grid>
                </Box>
              }
            />
          </main>
        </Box>
      )}
      <Dialog
        open={dialogType !== null}
        onClose={handleDialogClose}
        onConfirm={handleConfirm}
        title={
          dialogType === "delete" ? "Delete Confirmation" : "Paste Confirmation"
        }
        confirmButtonText={dialogType === "delete" ? "Delete" : "Paste"}
        cancelButtonText={!loading.update || !loading.delete ? "Cancel" : ""}
        icon={dialogType === "delete" ? <DeleteIcon /> : <ContentCopyRounded />}
        message={
          <Box padding={2}>
            {loading.update || loading.delete ? (
              <Loader
                color="primary"
                size={40}
              />
            ) : dialogType === "delete" ? (
              "Are you sure you want to delete this crop config?"
            ) : (
              "Are you sure you want to paste this data?"
            )}
          </Box>
        }
      />
    </>
  );
};

export default ConfigDetails;
